/**
 * Import Progress Indicator — the pieces every row shares.
 *
 * One visual language for queued, running, finished and failed imports: the
 * venue tile on the left carries the STATE in its ring (accent = running,
 * success, danger, plain border = queued), so a glance down the column reads
 * the whole batch without parsing any text.
 */

import { useEffect, useState } from 'react'

import { FileText, Plug, Wallet } from 'lucide-react'

/** What a row is showing — the one vocabulary of the panel. */
export type ImportRowState = 'queued' | 'running' | 'waiting' | 'done' | 'failed'

const RING_BY_STATE: Record<ImportRowState, string> = {
  queued: 'ring-crypto-border',
  running: 'ring-crypto-accent',
  waiting: 'ring-crypto-warning',
  done: 'ring-crypto-success',
  failed: 'ring-crypto-danger',
}

/** Text colour of a state label, theme-aware through the crypto-* tokens. */
export const STATE_TEXT_CLASS: Record<ImportRowState, string> = {
  queued: 'text-crypto-muted',
  running: 'text-crypto-accent',
  waiting: 'text-crypto-warning',
  done: 'text-crypto-success',
  failed: 'text-crypto-danger',
}

/** Fill of a state in the progress bars and the batch ribbon. */
export const STATE_FILL_CLASS: Record<ImportRowState, string> = {
  queued: 'bg-crypto-border',
  running: 'bg-crypto-accent',
  waiting: 'bg-crypto-warning',
  done: 'bg-crypto-success',
  failed: 'bg-crypto-danger',
}

/** Shared focus ring for the panel's buttons (keyboard users see where they are). */
export const FOCUS_RING_CLASS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-crypto-accent focus-visible:ring-offset-1 focus-visible:ring-offset-crypto-card'

/**
 * Two letters that stand for the venue: initials of a multi-word name, else its first two. A
 * parenthesised qualifier ("Kraken (API)") and punctuation are not part of the name.
 */
export const venueMonogram = (label: string): string => {
  const words = label
    .replace(/\([^)]*\)/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0] ?? ''
  const second = words[1] ?? ''
  const letters = second ? `${first.charAt(0)}${second.charAt(0)}` : first.slice(0, 2)
  return letters.toUpperCase()
}

const TYPE_ICON = { api: Plug, wallet: Wallet, csv: FileText } as const

interface VenueTileProps {
  label: string
  type?: string
  state: ImportRowState
}

/** The venue's monogram, with the import kind as a corner glyph and the state as its ring. */
export const VenueTile = ({ label, type, state }: VenueTileProps) => {
  const TypeIcon = type && type in TYPE_ICON ? TYPE_ICON[type as keyof typeof TYPE_ICON] : null
  return (
    <span
      aria-hidden="true"
      className={`relative flex-shrink-0 w-8 h-8 rounded-md bg-crypto-dark text-crypto-text text-xs font-semibold tracking-tight flex items-center justify-center ring-2 ${RING_BY_STATE[state]}`}
    >
      {venueMonogram(label)}
      {TypeIcon && (
        <span className="absolute -bottom-1 -right-1 rounded-full bg-crypto-card p-0.5 text-crypto-muted">
          <TypeIcon className="w-2.5 h-2.5" />
        </span>
      )}
    </span>
  )
}

/** `m:ss` under an hour, `h:mm:ss` past it. Negative spans (clock skew) read as zero. */
export const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** Current time, re-read every second while `ticking` (no timer at all otherwise). */
export const useNow = (ticking: boolean): number => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!ticking) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])
  return now
}
